import React, { memo, useCallback, useMemo } from 'react';
import { pick } from 'ramda';
import { clsx } from 'js/utils/classNames.util';
import { useAppSelector } from 'js/store/hooks';
import { Button } from 'js/components/common/Button';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { Label } from 'js/components/common/Fields/Label';
import { Radio } from 'js/components/common/Fields/Radio';
import { Input } from 'js/components/common/Fields/Input';
import { Select } from 'js/components/common/Fields/Select';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { DateInput } from 'js/components/common/Fields/DateInput';
import { AREA_CODES } from 'js/components/common/ChangeForm/constants';
import { useComponentTranslations, useComponentConfig } from 'js/hooks/component';
import { FORM_FIELD_TYPE, DATE_FIELD_DISPLAY_TYPE } from 'js/components/common/Form/constants';
import { DatePicker, convertISOToPicker, convertPickerToISO } from 'js/components/common/Fields/DatePicker';

import * as hooks from './hooks';
import * as helpers from '../../helpers';
import { IBANField } from '../IBANField';
import { SpanishIDField } from '../SpanishIDField';

import style from './ChangeFormField.scss';

export const ChangeFormField = memo(({
    field,
    disabled,
    formID = '',
    handleSubmit,
    isFieldHidden,
    setSubmitButton,
    validationConfig,
    className: classNameProp
}) => {
    const { componentId } = useComponentConfig();
    const translate = useComponentTranslations();
    const { allowedCurrencies, allowedCountries } = useAppSelector(pick(['allowedCurrencies', 'allowedCountries']));

    const className = useMemo(() => clsx([classNameProp, isFieldHidden && 'hidden']), [classNameProp, isFieldHidden]);

    const {
        isReCaptchaVisible,
        onReCaptchaTokenValidate,
        onReCaptchaTokenError,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    } = hooks.useRecaptchaValidation();

    const internalValidate = hooks.useInternalValidate({
        field,
        validationConfig,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    const {
        action,
        align,
        areaCode,
        baseType,
        confirmationInfoTooltip,
        confirmationLabel,
        confirmationPlaceholder,
        customList,
        defaultValue,
        displayType = DATE_FIELD_DISPLAY_TYPE.SELECTOR,
        hideFromUser,
        id,
        infoTooltip,
        inputMode,
        label,
        name,
        placeholder,
        readOnly,
        requiresConfirmation,
        selectedList,
        type,
        validationRules
    } = field;
    // formID is needed because component can have different forms in preferences
    const fieldId = `${componentId}_${formID}_${id}`;
    const {
        isLabelAsteriskEnabled,
        isPlaceholderAsteriskEnabled
    } = validationRules?.find(rule => rule.type === 'required') || {};

    const countriesList = useMemo(() => helpers.getCountriesList(allowedCountries), [allowedCountries]);

    const phoneAreaCodeList = useMemo(() => helpers.getPhoneAreaCodeList(allowedCountries), [allowedCountries]);

    const currenciesList = useMemo(() => helpers.getCurrenciesList(allowedCurrencies), [allowedCurrencies]);

    const preventInput = useCallback((event) => event.preventDefault(), []);

    // this hook is needed to pass information that exactly this button was clicked
    const onSubmitClick = useCallback((event) => {
        setSubmitButton?.(field.name);
        handleSubmit(event);
    }, [field, setSubmitButton, handleSubmit]);

    if (hideFromUser) {
        return <input
            type="hidden"
            name={name}
            value={defaultValue}
        />;
    }

    switch (type) {
        case FORM_FIELD_TYPE.TEXT_LABEL:
            return (
                <div className={`${style.textLabel} ${className}`} id={fieldId}>{translate(label)}</div>
            );
        case FORM_FIELD_TYPE.EMAIL:
        case FORM_FIELD_TYPE.USER_NAME:
            return (
                <>
                    <Input
                        id={fieldId}
                        name={name}
                        type={baseType}
                        disabled={disabled}
                        className={className}
                        label={label && translate(label)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        placeholder={placeholder && translate(placeholder)}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        aria-label={translate([label, placeholder, 'playtech.mobile.system.input.aria-label'])}
                        {...readOnly && { readOnly: true }}
                        {...inputMode && { inputMode }}
                        {...!isFieldHidden && { validate: internalValidate }}
                    />
                    {requiresConfirmation && (
                        <Input
                            type={type}
                            disabled={disabled}
                            className={className}
                            id={`${fieldId}_confirmation`}
                            name={`${name}_confirmation`}
                            isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                            label={confirmationLabel && translate(confirmationLabel)}
                            isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                            placeholder={confirmationPlaceholder && translate(confirmationPlaceholder)}
                            infoTooltip={confirmationInfoTooltip && translate(confirmationInfoTooltip)}
                            aria-label={translate([confirmationLabel, confirmationPlaceholder, 'playtech.mobile.system.input.aria-label'])}
                        />
                    )}
                    {isReCaptchaVisible && !isFieldHidden ? (
                        <div className={style.captcha}>
                            <ReCaptcha
                                operation="checkUsernameAvailability"
                                onValidate={onReCaptchaTokenValidate}
                                onError={onReCaptchaTokenError}
                            />
                        </div>
                    ) : null}
                </>
            );
        case FORM_FIELD_TYPE.TEXT:
        case FORM_FIELD_TYPE.PASSWORD:
            return <>
                <Input
                    id={fieldId}
                    type={type}
                    name={name}
                    disabled={disabled}
                    className={className}
                    label={label && translate(label)}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    placeholder={placeholder && translate(placeholder)}
                    infoTooltip={infoTooltip && translate(infoTooltip)}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    aria-label={translate([label, placeholder, 'playtech.mobile.system.input.aria-label'])}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                />
                {
                    requiresConfirmation &&
                    <Input
                        {...type === FORM_FIELD_TYPE.PASSWORD && {
                            onPaste: preventInput,
                            onDrop: preventInput,
                            onCopy: preventInput,
                            onCut: preventInput,
                            onContextMenu: preventInput
                        }}
                        type={type}
                        disabled={disabled}
                        className={className}
                        id={`${fieldId}_confirmation`}
                        name={`${name}_confirmation`}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        label={confirmationLabel && translate(confirmationLabel)}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        placeholder={confirmationPlaceholder && translate(confirmationPlaceholder)}
                        infoTooltip={confirmationInfoTooltip && translate(confirmationInfoTooltip)}
                        aria-label={translate([confirmationLabel, confirmationPlaceholder, 'playtech.mobile.system.input.aria-label'])}
                    />
                }
            </>;
        case FORM_FIELD_TYPE.IBAN:
            return <IBANField
                name={name}
                id={fieldId}
                disabled={disabled}
                className={className}
                label={label && translate(label)}
                isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                placeholder={placeholder && translate(placeholder)}
                infoTooltip={infoTooltip && translate(infoTooltip)}
                isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                ariaLabel={translate([label, placeholder, 'playtech.mobile.system.input.aria-label'])}
                {...readOnly && { readOnly: true }}
                {...!isFieldHidden && { validate: internalValidate }}
            />;
        case FORM_FIELD_TYPE.SPANISH_ID:
            return <SpanishIDField
                id={fieldId}
                type={type}
                name={name}
                disabled={disabled}
                className={className}
                isFieldHidden={isFieldHidden}
                label={label && translate(label)}
                isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                placeholder={placeholder && translate(placeholder)}
                infoTooltip={infoTooltip && translate(infoTooltip)}
                isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                aria-label={translate([label, placeholder, 'playtech.mobile.system.input.aria-label'])}
                {...readOnly && { readOnly: true }}
                {...inputMode && { inputMode }}
                {...!isFieldHidden && { validate: internalValidate }}
            />;

        case FORM_FIELD_TYPE.CHECKBOX:
            return <Checkbox
                name={name}
                id={fieldId}
                disabled={disabled}
                className={className}
                label={label && translate(label)}
                isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                aria-label={translate([label, 'playtech.mobile.system.checkbox.aria-label'])}
                {...readOnly && { readOnly: true }}
            />;

        case FORM_FIELD_TYPE.RADIO_BUTTON:
            return <>
                {(label || isLabelAsteriskEnabled) && (
                    <Label
                        id={fieldId}
                        label={label && translate(label)}
                        className={isFieldHidden ? 'hidden' : ''}
                        classNames={{ label: style.radioGroupLabel }}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                    />
                )}
                {customList.map((item) => (
                    <Radio
                        name={name}
                        key={item.id}
                        value={item.value}
                        disabled={disabled}
                        className={className}
                        label={item.key && translate(item.key)}
                        aria-label={translate([item.key, 'playtech.mobile.system.radio.aria-label'])}
                        {...readOnly && { readOnly: true }}
                    />
                ))}
            </>;

        case FORM_FIELD_TYPE.COUNTRY:
        case FORM_FIELD_TYPE.CURRENCY_CODE:
        case FORM_FIELD_TYPE.DROPDOWN: {
            const selectedArray = (() => {
                switch (selectedList) {
                    case 'countries': return countriesList;
                    case 'currencies': return currenciesList;
                    default:
                        return customList.map(({
                            key,
                            value
                        }) => ({
                            label: translate(key, key),
                            value
                        }));
                }
            })();

            return <Select
                id={fieldId}
                type={type}
                name={name}
                disabled={disabled}
                className={className}
                options={selectedArray}
                label={label && translate(label)}
                isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                placeholder={placeholder && translate(placeholder)}
                infoTooltip={infoTooltip && translate(infoTooltip)}
                aria-label={translate([label, placeholder, 'playtech.mobile.system.select.aria-label'])}
                isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                {...readOnly && { readOnly: true }}
            />;
        }

        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH: {
            return displayType === DATE_FIELD_DISPLAY_TYPE.SELECTOR
                ? (
                    <DatePicker
                        name={name}
                        disabled={disabled}
                        className={className}
                        parse={convertPickerToISO}
                        format={convertISOToPicker}
                        label={label && translate(label)}
                        range={helpers.getRangeFromValidation(field)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                        {...readOnly && { readOnly: true }}
                    />
                ) : (
                    <DateInput
                        name={name}
                        id={fieldId}
                        disabled={disabled}
                        className={className}
                        label={label && translate(label)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        infoTooltip={infoTooltip && translate(infoTooltip)}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        {...readOnly && { readOnly: true }}
                    />
                );
        }

        case FORM_FIELD_TYPE.BUTTON:
            return <div className={clsx([className, style[align]])}>
                <Button
                    key={id}
                    id={fieldId}
                    type="button"
                    disabled={disabled}
                    className={style.submit}
                    {...action === 'submit' && { onClick: onSubmitClick }}
                >
                    {translate(label)}
                </Button>
            </div>;

        case FORM_FIELD_TYPE.CELLPHONE:
        case FORM_FIELD_TYPE.PHONE: {
            return (
                <div className={style.phoneContainer}>
                    {label && (
                        <Label
                            id={fieldId}
                            label={label && translate(label)}
                            {...readOnly && { readOnly: true }}
                            isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                            infoTooltip={infoTooltip && translate(infoTooltip)}
                        />
                    )}
                    <div className={style.phone}>
                        {
                            areaCode !== AREA_CODES.HIDDEN && (
                                <Select
                                    id={`${fieldId}-areaCode`}
                                    name={`${name}-areaCode`}
                                    options={phoneAreaCodeList}
                                    className={`${className} ${style.areaCode}`}
                                    disabled={disabled || areaCode === AREA_CODES.PREDEFINED || readOnly}
                                    aria-label={translate([label, placeholder, 'playtech.mobile.system.select.aria-label'])}
                                    classNames={{
                                        validationIcon: style.validationIcon,
                                        content: `${style.content} ${disabled || areaCode === AREA_CODES.PREDEFINED || readOnly ? style.disabled : ''}`
                                    }}
                                />
                            )
                        }
                        {
                            areaCode === AREA_CODES.HIDDEN && (
                                <input
                                    type="hidden"
                                    name={`${name}-areaCode`}
                                />
                            )
                        }
                        <Input
                            id={fieldId}
                            name={name}
                            type={baseType}
                            disabled={disabled}
                            {...readOnly && { readOnly: true }}
                            placeholder={placeholder && translate(placeholder)}
                            isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                            aria-label={translate([label, placeholder, 'playtech.mobile.system.input.aria-label'])}
                            className={`${className} ${style.phoneInput} ${areaCode === AREA_CODES.HIDDEN ? style.noAreaCodePhoneInput : ''}`}
                        />
                    </div>
                </div>
            );
        }
        default:
            return null;
    }
});
